/**
 * Where each student stands on standups, for the coach list and its CSV.
 * Both read this one function so the screen and the download always agree.
 */

export type RosterStatus = 'posting' | 'quiet' | 'away' | 'notDue';

export const ROSTER_STATUS_LABELS: Record<RosterStatus, string> = {
  posting: 'Posting',
  quiet: 'In lessons, not posting',
  away: 'No activity',
  notDue: 'Not due',
};

/** How far back the status looks, in days, counting today. */
export const ROSTER_STATUS_DAYS = 7;

/**
 * Posting: a standup on any of the student's last seven local days.
 * Not due: nothing posted, and none of those days was theirs to answer
 * (before standups open, just joined, or after they close). Only judged when
 * `dueDays` is known: without a standup start date the grid owes nothing, so
 * the status falls through to lesson activity as before.
 * In lessons, not posting: a standup was due, none posted, but they
 * opened a lesson in the last seven days. No activity: neither.
 *
 * `localToday` is the student's own date, so the standup check uses the same
 * calendar their standups are stamped with. `dueDays` are the days the grid
 * holds against the student (see isDueDay), so this never flags a student the
 * grid would not mark missed.
 */
export function rosterStatus(opts: {
  dates: string[];
  dueDays?: string[];
  localToday: string;
  lastLessonVisitAt?: Date;
  now: Date;
}): RosterStatus {
  const windowStart = new Date(`${opts.localToday}T00:00:00Z`);
  windowStart.setUTCDate(windowStart.getUTCDate() - (ROSTER_STATUS_DAYS - 1));
  const since = windowStart.toISOString().slice(0, 10);
  const isRecent = (date: string) => date >= since && date <= opts.localToday;
  if (opts.dates.some(isRecent)) {
    return 'posting';
  }
  if (opts.dueDays && !opts.dueDays.some(isRecent)) {
    return 'notDue';
  }
  const cutoff = opts.now.getTime() - ROSTER_STATUS_DAYS * 24 * 60 * 60 * 1000;
  if (opts.lastLessonVisitAt && opts.lastLessonVisitAt.getTime() >= cutoff) {
    return 'quiet';
  }
  return 'away';
}

/** The most recent date in a list of YYYY-MM-DD dates. */
export function latestDate(dates: string[]): string | undefined {
  return dates.reduce<string | undefined>((max, d) => (!max || d > max ? d : max), undefined);
}
